/**
 * /transit/:transitionId — a transition's workspace, laid out like the
 * prototype's: header, one tab per node (plus a Map of the whole graph), the
 * selected node's card, and the transition's documents. A node's questions
 * open in the right-hand QuestionsPanel (?panel=questions).
 *
 * All documents go in first; every node whose dependencies are done can then
 * draft at once, and what the documents don't answer comes back as questions.
 * The drafting engine is not built yet, so nodes show their state but nothing
 * drafts.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams, useParams } from 'react-router-dom'
import { ArrowRight, Check, CircleCheck, ChevronDown, FileText, Gauge, Hourglass, Loader2, MessageCircleQuestion, Network, Sparkles, X } from 'lucide-react'
import { Button, Textarea } from '../../components/ui'
import NodeGraph, { NodeGraphLegend } from '../../components/transit/NodeGraph'
import QuestionsPanel, { type Draft as AnswerDraft } from '../../components/transit/QuestionsPanel'
import SidePanel from '../../components/transit/SidePanel'
import TransitionContextModal from '../../components/transit/TransitionContextModal'
import CostFigures, { figureCounts } from '../../components/transit/CostFigures'
import SvgIcon from '../../components/transit/SvgIcon'
import StatusIcon from '../../components/transit/StatusIcon'
import { nodeChipClass, nodeStatusDetail, nodeStatusLabel, timeAgo, TRANSIT_GRAPH, type TransitGraphEvent } from '../../components/transit/status'
import { inDisplayOrder, MAP, PACKAGE, READINESS } from '../../components/transit/displayOrder'
import PackageSection from '../../components/transit/PackageSection'
import { cn } from '../../lib/utils'
import { transitApi } from '../../services/transit'
import type {
  DraftBlock, GraphNode, Readiness, InfoRequest, TransitDocument, Transition, TransitionGraph,
} from '../../types/transit'

const QUESTIONS = 'questions'
const FIGURES = 'figures'
// Unsent answers in the questions panel, kept for this browser tab so a
// reload, a section switch or Hide doesn't throw typing away.
const ANSWER_DRAFTS = 'transit.answerDrafts'
const HIDDEN = 'hidden'

export default function TransitWorkspace() {
  const { transitionId } = useParams<{ transitionId: string }>()
  const [params, setParams] = useSearchParams()
  const [transition, setTransition] = useState<Transition | null>(null)
  const [graph, setGraph] = useState<TransitionGraph | null>(null)
  const [requests, setRequests] = useState<InfoRequest[]>([])
  const [error, setError] = useState<string | null>(null)
  // The Transition Context modal: the transition's documents.
  const [contextOpen, setContextOpen] = useState(false)
  const [answerDrafts, setAnswerDrafts] = useState<Record<string, AnswerDraft>>(() => {
    try {
      return JSON.parse(sessionStorage.getItem(ANSWER_DRAFTS) ?? '{}')
    } catch {
      return {}
    }
  })
  useEffect(() => {
    try {
      sessionStorage.setItem(ANSWER_DRAFTS, JSON.stringify(answerDrafts))
    } catch {
      // Storage blocked: the drafts still live for this page.
    }
  }, [answerDrafts])

  // Questions a draft raised after the page loaded, by section key: an alert
  // under the bar until the section's questions are open or it's dismissed.
  // seenOpen holds every open draft question already accounted for.
  const seenOpen = useRef<Set<string> | null>(null)
  const [arrived, setArrived] = useState<Record<string, number>>({})
  useEffect(() => { seenOpen.current = null; setArrived({}) }, [transitionId])
  // The transition on screen, so a load that comes back after the user moved
  // to another one is dropped instead of filling this one's state.
  const onScreen = useRef(transitionId)
  onScreen.current = transitionId

  const load = useCallback(async () => {
    if (!transitionId) return
    try {
      const [t, g, r] = await Promise.all([
        transitApi.getTransition(transitionId),
        transitApi.getGraph(transitionId),
        transitApi.listInfoRequests(transitionId),
      ])
      if (onScreen.current !== transitionId) return
      setTransition(t); setGraph(g); setRequests(r); setError(null)
      const open = r.filter((q) => q.status === 'open' && q.origin === 'draft')
      const seen = seenOpen.current
      const fresh = seen ? open.filter((q) => !seen.has(q.id)) : []
      if (fresh.length) {
        const keyById = new Map(g.nodes.map((n) => [n.id, n.node_key]))
        setArrived((all) => {
          const next = { ...all }
          for (const q of fresh) {
            const key = keyById.get(q.transition_node_id)
            if (key) next[key] = (next[key] ?? 0) + 1
          }
          return next
        })
      }
      seenOpen.current = new Set([...(seen ?? []), ...open.map((q) => q.id)])
      // The sidebar's row for this transition shows the same statuses.
      window.dispatchEvent(new CustomEvent<TransitGraphEvent>(TRANSIT_GRAPH, { detail: { transitionId, nodes: g.nodes } }))
    } catch (e: any) {
      setError(e.message)
    }
  }, [transitionId])

  useEffect(() => { load() }, [load])

  // Drafting runs in the background (one River job per node). Refresh while a
  // node is drafting, and for a short window after anything was queued (a job
  // can take a moment to claim its node).
  const [watchUntil, setWatchUntil] = useState(0)
  // Re-render each minute so the "… ago" times age while the page sits open.
  const [, setTick] = useState(0)
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60000)
    return () => clearInterval(id)
  }, [])
  const watch = () => setWatchUntil(Date.now() + 8000)
  const drafting = graph?.nodes.some((n) => n.status === 'drafting') ?? false
  useEffect(() => {
    if (!drafting && Date.now() > watchUntil) return
    const id = setInterval(load, 1500)
    const end = setTimeout(() => setWatchUntil(0), Math.max(0, watchUntil - Date.now()))
    return () => { clearInterval(id); clearTimeout(end) }
  }, [drafting, watchUntil, load])

  // Drafting starts on its own once every document is read and sorted:
  // drafting skips a document still being read and takes an unsorted one as
  // vendor-supplied. POST /transitions/{id}/draft queues every ready section.
  const hasReady = !!graph?.nodes.some((n) => n.status === 'ready')
  const [inputs, setInputs] = useState<TransitDocument[] | null>(null)
  const loadInputs = useCallback(async () => {
    if (!transitionId) return
    try {
      setInputs((await transitApi.listDocuments(transitionId)).filter((d) => d.kind !== 'generated'))
    } catch {
      // The next poll tries again.
    }
  }, [transitionId])
  useEffect(() => { if (hasReady) loadInputs() }, [hasReady, loadInputs])
  const reading = inputs?.filter((d) => d.status === 'pending' || d.status === 'extracting').length ?? 0
  const sorting = !!inputs?.some((d) => d.status === 'ready' && d.kind === 'uploaded' && !d.document_type && Date.now() - Date.parse(d.updated_at) < 30_000)
  const settled = inputs !== null && reading === 0 && !sorting
  useEffect(() => {
    if (!hasReady || settled) return
    const id = setInterval(loadInputs, 2000)
    return () => clearInterval(id)
  }, [hasReady, settled, loadInputs])
  const started = useRef(false)
  useEffect(() => { started.current = false }, [transitionId])
  const startDrafting = useCallback(async () => {
    if (!transitionId || started.current) return
    started.current = true
    try {
      await transitApi.draftReady(transitionId)
      setWatchUntil(Date.now() + 8000)
      load()
    } catch (e: any) {
      started.current = false
      setError(e.message)
    }
  }, [transitionId, load])
  useEffect(() => {
    if (hasReady && settled) startDrafting()
  }, [hasReady, settled, startDrafting])

  const openByNode = useMemo(() => {
    const keyById = new Map(graph?.nodes.map((n) => [n.id, n.node_key]))
    const m = new Map<string, number>()
    for (const r of requests) {
      const key = keyById.get(r.transition_node_id)
      // Only questions a draft raised hold a section; intake ones are optional.
      if (r.status === 'open' && r.origin === 'draft' && key) m.set(key, (m.get(key) ?? 0) + 1)
    }
    return m
  }, [graph, requests])

  // Looking at a section with its questions showing answers its alert: the
  // section on screen (the first one when ?node= is unset), with the panel on
  // its questions, not hidden and not on Cost figures (the panel's own rule).
  const viewing = params.get('node') ?? (graph ? inDisplayOrder(graph.nodes)[0]?.node_key : undefined)
  const viewingNode = graph?.nodes.find((n) => n.node_key === viewing)
  const panelParam = params.get('panel')
  const questionsShowing = !!viewingNode && panelParam !== HIDDEN && !(panelParam === FIGURES && figureCounts(viewingNode).total > 0)
  useEffect(() => {
    if (viewing && arrived[viewing] && questionsShowing) {
      setArrived(({ [viewing]: _, ...rest }) => rest)
    }
  }, [viewing, arrived, questionsShowing])

  // Only a first load that failed replaces the page. Once it has loaded, an
  // error (a refresh during drafting, a failed action) shows as a line, so
  // the panel and anything typed in it stay put.
  if (error && (!transition || !graph)) return <div className="p-8 text-destructive">{error}</div>
  if (!transition || !graph) {
    return (
      <div className="flex items-center justify-center h-96 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading transition…
      </div>
    )
  }

  const tab = params.get('node') ?? inDisplayOrder(graph.nodes)[0]?.node_key ?? MAP
  const setParam = (name: string, value: string | null) => setParams((p) => {
    const next = new URLSearchParams(p)
    if (value === null) next.delete(name)
    else next.set(name, value)
    return next
  }, { replace: true })
  // ?panel=questions (opened) carries over to the next node; ?panel=hidden
  // (Hide) applies to this node only, so the next one can pop open again.
  const select = (key: string) => setParams((p) => {
    const next = new URLSearchParams(p)
    next.set('node', key)
    if (next.get('panel') === HIDDEN) next.delete('panel')
    return next
  }, { replace: true })
  const node = graph.nodes.find((n) => n.node_key === tab) ?? null
  const nodeRequests = node ? requests.filter((r) => r.transition_node_id === node.id) : []
  // The panel pops open by itself while the node has unanswered questions,
  // unless the user hid it.
  const panel = params.get('panel')
  const figures = node ? figureCounts(node) : { total: 0, confirmed: 0 }
  // The panel opens by itself, unless hidden: on unanswered questions first,
  // else on a section's cost figures (confirmed or not: Cost Roll-Up is where
  // they're reviewed).
  const openQuestions = nodeRequests.some((r) => r.status === 'open')
  const hasFigures = figures.total > 0
  const panelOpen = node !== null &&
    (panel === QUESTIONS || panel === FIGURES || (panel !== HIDDEN && (openQuestions || hasFigures)))
  const panelTab =
    panel === FIGURES ? (hasFigures ? FIGURES : QUESTIONS)
      : panel === QUESTIONS || openQuestions ? QUESTIONS
        : hasFigures ? FIGURES : QUESTIONS
  const draftingCount = graph.nodes.filter((n) => n.status === 'drafting').length

  return (
    <div className="flex h-full min-h-0">
    <div className="min-w-0 flex-1 overflow-y-auto">
      {/* Bridge "New navigation" (394:5647): transition / section, the section
          switched from its menu; it stays put as the page scrolls. */}
      <div className="sticky top-0 z-10 bg-card">
      <header className="flex items-center justify-between gap-4 px-8 py-4">
        <SectionCrumb transitionName={transition.name} graph={graph} tab={tab} openByNode={openByNode} onSelect={select} />
        {/* Bridge "Technical Readiness, Empty" (524:5984): the documents, by what they are. */}
        <button type="button" data-tour="context" onClick={() => setContextOpen(true)}
          className="flex h-8 min-w-16 shrink-0 items-center justify-center gap-1 rounded-[6px] border border-input px-3 text-sm font-medium leading-4 text-foreground transition-colors hover:bg-secondary">
          <SvgIcon src="/transit/icon-context.svg" width={16} height={16} /> Transition Context
        </button>
      </header>
      <QuestionsAlert arrived={arrived} graph={graph} onDismiss={() => setArrived({})}
        onAnswer={(key) => { setArrived(({ [key]: _, ...rest }) => rest); setParams((p) => {
          const next = new URLSearchParams(p)
          next.set('node', key)
          next.set('panel', QUESTIONS)
          return next
        }, { replace: true }) }} />
      </div>
    <div className="px-8 pb-6 pt-10 max-w-6xl mx-auto space-y-8">
      {/* The documents being read and sorted until drafting starts (on its
          own, above), then how many sections are drafting. */}
      {hasReady && inputs !== null && !settled ? (
        <p role="status" className="flex items-center gap-2 rounded-md bg-muted px-4 py-3 text-sm text-foreground">
          <Loader2 className="h-4 w-4 shrink-0 animate-spin text-brand" aria-hidden />
          {reading > 0
            ? `Reading your documents: ${inputs.length - reading} of ${inputs.length} read. Drafting starts on its own once they're in.`
            : 'Sorting your documents. Drafting starts in a moment.'}
        </p>
      ) : draftingCount > 0 && (
        <p role="status" className="flex items-center justify-end gap-2 text-sm text-tertiary">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Drafting {draftingCount} section{draftingCount === 1 ? '' : 's'}…
        </p>
      )}
      {error && <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}

      {tab === READINESS ? (
        <ReadinessCard transitionId={transition.id} />
      ) : tab === PACKAGE ? (
        <PackageView transitionId={transition.id} graph={graph} onSelect={select}
          onOpenFigures={(key) => setParams((p) => {
            const next = new URLSearchParams(p)
            next.set('node', key)
            next.set('panel', FIGURES)
            return next
          }, { replace: true })} />
      ) : tab === MAP ? (
        <section data-tour="map" className="rounded-xl border border-border bg-card p-5 space-y-3">
          <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between md:gap-6">
            <div className="min-w-0 flex-1">
              <h2 className="font-semibold text-foreground">How the package fits together</h2>
              <p className="text-sm text-muted-foreground">
                Sections draft in steps: everything in a step drafts together, once the sections it waits on are approved.
                Hover over a section to see what it depends on and which sections depend on it.
              </p>
            </div>
            <div className="shrink-0 md:pt-1"><NodeGraphLegend /></div>
          </div>
          <NodeGraph nodes={graph.nodes} edges={graph.edges} selected={null} onSelect={select} openRequests={openByNode} />
        </section>
      ) : node ? (
        <NodeCard node={node} graph={graph} requests={nodeRequests} openByNode={openByNode} showing={panelOpen ? panelTab : null}
          onOpenQuestions={() => setParam('panel', QUESTIONS)}
          onOpenFigures={() => setParam('panel', FIGURES)}
          onChange={() => { watch(); load() }} onSelect={select} transitionId={transition.id} onError={setError} />
      ) : null}

    </div>
    </div>
    {contextOpen && (
      <TransitionContextModal transitionId={transition.id} sections={inDisplayOrder(graph.nodes)} onClose={() => { setContextOpen(false); if (hasReady) loadInputs() }} />
    )}
    {panelOpen && node && (
      <SidePanel title={panelTab === FIGURES ? 'Cost figures' : 'Questionnaire'} onHide={() => setParam('panel', HIDDEN)}>
        {panelTab === FIGURES ? (
          <CostFigures key={node.id} node={node} onChange={() => { setParam('panel', FIGURES); watch(); load() }} />
        ) : (
          <QuestionsPanel key={node.id} node={node} requests={nodeRequests} drafts={answerDrafts} setDrafts={setAnswerDrafts}
            // Only the sections it still waits on: approved ones are done with.
            waitsOn={graph.edges.filter((e) => e.node_key === node.node_key)
              .map((e) => graph.nodes.find((n) => n.node_key === e.depends_on_key))
              .filter((n): n is GraphNode => !!n && n.status !== 'done').map((n) => n.name)}
            // Once answering starts, keep it open: answering the last question
            // must not close it before "Answers submitted" shows.
            onChange={() => { setParam('panel', QUESTIONS); watch(); load() }} />
        )}
      </SidePanel>
    )}
    </div>
  )
}

/**
 * New questions from a draft, wherever the user is on the page: the attention
 * yellow of the "Needs input" chips, pinned under the bar, naming the sections
 * and opening the first one's questions.
 */
function QuestionsAlert({ arrived, graph, onAnswer, onDismiss }: {
  arrived: Record<string, number>; graph: TransitionGraph; onAnswer: (key: string) => void; onDismiss: () => void
}) {
  const sections = inDisplayOrder(graph.nodes).filter((n) => arrived[n.node_key])
  if (sections.length === 0) return null
  const total = sections.reduce((sum, n) => sum + arrived[n.node_key], 0)
  const what = `${total} new question${total === 1 ? '' : 's'}`
  return (
    <div role="alert" className="mx-8 mb-3 flex animate-fade-in items-center gap-3 rounded-md bg-status-attention/60 px-4 py-2.5 text-sm leading-5 text-status-foreground dark:bg-status-attention">
      <MessageCircleQuestion className="h-4 w-4 shrink-0" aria-hidden />
      <p className="min-w-0 flex-1">
        {sections.length === 1
          ? <><span className="font-medium">{sections[0].name}</span> has {what}. Its draft waits for your answers.</>
          : <><span className="font-medium">{what}</span> in {sections.map((n) => n.name).join(', ')}.</>}
      </p>
      <button type="button" onClick={() => onAnswer(sections[0].node_key)}
        className="shrink-0 rounded-[6px] bg-status-foreground px-3 py-1.5 text-xs font-medium text-status-attention hover:opacity-90">
        {sections.length === 1 ? 'Answer them' : `Answer ${sections[0].name}`}
      </button>
      <button type="button" onClick={onDismiss} aria-label="Dismiss" className="shrink-0 rounded p-1 hover:bg-status-foreground/10">
        <X className="h-4 w-4" aria-hidden />
      </button>
    </div>
  )
}

/**
 * "Transition / Section ▾": the current section, and a menu to switch to any
 * other (with its status and open questions), Readiness or Map.
 */
function SectionCrumb({ transitionName, graph, tab, openByNode, onSelect }: {
  transitionName: string; graph: TransitionGraph; tab: string; openByNode: Map<string, number>; onSelect: (key: string) => void
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('mousedown', onClick); document.removeEventListener('keydown', onKey) }
  }, [open])
  const current = tab === READINESS ? 'Readiness' : tab === MAP ? 'Map' : tab === PACKAGE ? 'Generate package'
    : graph.nodes.find((n) => n.node_key === tab)?.name ?? ''
  const go = (key: string) => { onSelect(key); setOpen(false) }
  // Each item: an icon, the name, and under it where that step stands.
  const item = (active: boolean) => cn(
    'flex w-full items-start gap-2 px-3 py-2 text-left text-sm text-popover-foreground transition-colors',
    active ? 'bg-secondary' : 'hover:bg-accent'
  )
  const label = (name: string, detail: string, active: boolean) => (
    <span className="flex min-w-0 flex-1 flex-col">
      <span className={cn('leading-5', active && 'font-medium')}>{name}</span>
      <span className="text-xs leading-4 text-tertiary">{detail}</span>
    </span>
  )
  const approved = graph.nodes.filter((n) => n.status === 'done').length
  return (
    <nav aria-label="Breadcrumb" data-tour="sections" className="flex min-w-0 items-center gap-4">
      {/* The design system's Breadcrumb: the trail in text/secondary, the
          current page in text/primary, both Regular 14. */}
      <span className="truncate text-sm leading-6 text-muted-foreground">{transitionName}</span>
      <span className="text-lg leading-6 text-tertiary" aria-hidden>/</span>
      <div ref={ref} className="relative shrink-0">
        <button type="button" onClick={() => setOpen((v) => !v)} aria-haspopup="menu" aria-expanded={open}
          className="-mx-1 flex items-center gap-2 rounded-md px-1 text-sm leading-6 text-foreground hover:bg-secondary">
          {current}
          <SvgIcon src="/transit/icon-chevron-down.svg" width={20} height={20} className={cn('transition-transform', open && 'rotate-180')} />
        </button>
        {open && (
          // As wide as the longest name (at least 18rem, at most 36rem, then it wraps).
          <div role="menu" className="absolute left-0 top-full z-20 mt-2 max-h-[calc(var(--app-height)-8rem)] w-max min-w-72 max-w-[36rem] overflow-y-auto rounded-md border border-border bg-popover py-1 shadow-lg">
            {inDisplayOrder(graph.nodes).map((n) => (
              <button key={n.node_key} type="button" role="menuitem" onClick={() => go(n.node_key)} className={item(tab === n.node_key)}>
                <StatusIcon status={n.status} labelled className="mt-[3px]" />
                {label(n.name, nodeStatusDetail(n.status, openByNode.get(n.node_key) ?? 0), tab === n.node_key)}
              </button>
            ))}
            <div className="my-1 border-t border-border" />
            <button type="button" role="menuitem" onClick={() => go(READINESS)} className={item(tab === READINESS)}>
              <Gauge className="mt-[3px] h-3.5 w-3.5 shrink-0" />
              {label('Readiness', 'TRL, MRL, IRL and CRL, from your documents', tab === READINESS)}
            </button>
            <button type="button" role="menuitem" onClick={() => go(MAP)} className={item(tab === MAP)}>
              <Network className="mt-[3px] h-3.5 w-3.5 shrink-0" />
              {label('Map', 'Which sections wait on which', tab === MAP)}
            </button>
            <button type="button" role="menuitem" onClick={() => go(PACKAGE)} className={item(tab === PACKAGE)}>
              <FileText className="mt-[3px] h-3.5 w-3.5 shrink-0" />
              {label('Generate package', approved === graph.nodes.length ? 'All sections approved: ready to generate'
                : `${approved} of ${graph.nodes.length} sections approved`, tab === PACKAGE)}
            </button>
          </div>
        )}
      </div>
    </nav>
  )
}

/**
 * One section of the graph (a node), laid out after the Bridge section page:
 * title and status, the actions the API allows in this status, the guided
 * questions, then the draft rendered from its blocks.
 */
function NodeCard({ node, graph, requests, openByNode, showing, onOpenQuestions, onOpenFigures, onChange, onSelect, transitionId, onError }: {
  node: GraphNode; graph: TransitionGraph; requests: InfoRequest[]
  /** Open draft questions per section, for what a waited-on section needs. */
  openByNode: Map<string, number>
  /** What the side panel shows, if it's open: that action item's card is hidden, it's already open. */
  showing: string | null
  onOpenQuestions: () => void; onOpenFigures: () => void; onChange: () => void
  onSelect: (key: string) => void; transitionId: string; onError: (msg: string) => void
}) {
  const [busy, setBusy] = useState(false)
  const [editing, setEditing] = useState(false)
  const [open, setOpen] = useState(true)
  useEffect(() => { setEditing(false); setOpen(true) }, [node.node_key])
  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true)
    try {
      await fn()
      onChange()
    } catch (e: any) {
      onError(e.message)
    } finally {
      setBusy(false)
    }
  }
  const byKey = new Map(graph.nodes.map((n) => [n.node_key, n]))
  const waitsOn = graph.edges.filter((e) => e.node_key === node.node_key).map((e) => byKey.get(e.depends_on_key)).filter(Boolean) as GraphNode[]
  const feeds = graph.edges.filter((e) => e.depends_on_key === node.node_key).map((e) => byKey.get(e.node_key)).filter(Boolean) as GraphNode[]
  const unanswered = requests.filter((r) => r.status === 'open').length
  const figures = figureCounts(node)
  const unconfirmed = figures.total - figures.confirmed
  const questionsCard = requests.length > 0 && showing !== QUESTIONS
  const figuresCard = figures.total > 0 && showing !== FIGURES
  const approvalCard = node.status === 'review'
  const inOrder = inDisplayOrder(graph.nodes)
  const nextSection = inOrder[inOrder.findIndex((n) => n.node_key === node.node_key) + 1]
  const blockedCard = node.status === 'blocked' && waitsOn.some((n) => n.status !== 'done')
  // The statuses the API lets a person act on a draft in.
  const settled = (['review', 'needs_input', 'failed', 'done'] as const).includes(node.status as 'review')
  const button = 'h-10 min-w-20 rounded-[6px] px-4 text-sm shadow-none'

  return (
    <section className="flex flex-col gap-8">
      <div className="flex flex-col gap-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex min-w-0 flex-col gap-2">
            <h2 className="text-2xl font-semibold leading-[1.2] text-foreground">{node.name}</h2>
            {node.description && <p className="text-sm text-tertiary">{node.description}</p>}
          </div>
          <span data-tour="status" className={cn('inline-flex shrink-0 items-center gap-1.5 rounded-[4px] px-2 py-1 text-sm leading-4', nodeChipClass(node.status))}>
            <StatusIcon status={node.status} plain labelled />
            {nodeStatusLabel(node.status)}
          </span>
        </div>

        {(waitsOn.length > 0 || feeds.length > 0) && (
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-tertiary">
            {waitsOn.length > 0 && <NodeLinks label="Waits on" nodes={waitsOn} onSelect={onSelect} />}
            {feeds.length > 0 && <NodeLinks label="Feeds" nodes={feeds} onSelect={onSelect} />}
          </div>
        )}

        {(settled || node.status === 'ready') && (
          <div className="flex flex-wrap gap-1">
            {settled && !!node.data?.blocks?.length && !editing && (
              <Button variant="outline" disabled={busy} onClick={() => setEditing(true)}
                className={cn(button, 'border-input bg-card text-foreground hover:bg-secondary')}>
                Edit section text
              </Button>
            )}
            {settled && (
              <Button variant={node.status === 'review' ? 'secondary' : 'default'} disabled={busy}
                onClick={() => act(() => transitApi.redraftNode(transitionId, node.node_key))}
                className={cn(button, node.status === 'review' && 'hover:bg-secondary-hover')}>
                Let Overmatch redraft this section
              </Button>
            )}
            {node.status === 'ready' && (
              <Button disabled={busy} onClick={() => act(() => transitApi.redraftNode(transitionId, node.node_key))} className={button}>
                <Sparkles className="h-3.5 w-3.5" /> Let Overmatch draft this section
              </Button>
            )}
          </div>
        )}
      </div>

      {(approvalCard || blockedCard || questionsCard || figuresCard) && (
        <div className="flex flex-col gap-3">
          <p className="text-base font-medium leading-4 text-tertiary">Action items</p>
          <div className="flex flex-wrap gap-3">
            {approvalCard && (
              <ApprovalNotice />
            )}
            {blockedCard && (
              <BlockedNotice waitsOn={waitsOn} openByNode={openByNode} onSelect={onSelect} />
            )}
            {questionsCard && (
              <ActionCard label="Guided questions" title={`${node.name} Setup`} onOpen={onOpenQuestions}
                body={`${unanswered > 0 ? `${unanswered} of ${requests.length} unanswered. ` : 'All answered or skipped. '}${node.description}`} />
            )}
            {figuresCard && (
              <ActionCard label="Cost figures" title={unconfirmed > 0 ? 'Confirm cost figures' : 'Review cost figures'} onOpen={onOpenFigures}
                body={unconfirmed > 0
                  ? `${unconfirmed} of ${figures.total} figures unconfirmed. Check each against its source; totals come from these rows.`
                  : `All ${figures.total} figures confirmed. Open them to review or change a confirmation.`} />
            )}
          </div>
        </div>
      )}

      {node.error && (
        <div className="flex flex-col gap-1 text-sm">
          <p className={node.status === 'failed' ? 'text-destructive' : 'text-tertiary'}>{node.error}</p>
        </div>
      )}

      <div data-tour="draft" className="flex flex-col rounded-lg border border-input bg-background p-1">
        <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}
          className="flex h-14 items-center justify-between rounded-md px-4 text-left">
          <span className="flex items-center gap-1 text-lg font-medium leading-6 text-foreground">
            <ChevronDown className={cn('h-5 w-5 transition-transform', !open && '-rotate-90')} aria-hidden />
            Draft
          </span>
          {node.revision > 0 && <span className="text-sm text-tertiary">Revision {node.revision}</span>}
        </button>
        {open && (
          <div className="flex flex-col gap-4 px-4 pb-4">
            {editing && node.data?.blocks && !(node.status === 'drafting' && !node.content) ? (
              <BlockEditor blocks={node.data.blocks} busy={busy} onCancel={() => setEditing(false)}
                onSave={(blocks) => act(async () => { await transitApi.editNode(transitionId, node.node_key, node.revision, blocks); setEditing(false) })} />
            ) : (
              <DraftBody node={node} onReviewFigures={onOpenFigures} />
            )}
            {node.sources.length > 0 && (
              <div className="flex flex-col gap-1 border-t border-border-subtle pt-3 text-sm">
                <p className="font-medium text-tertiary">Sources</p>
                {node.sources.map((s, i) => (
                  <p key={i} className="text-muted-foreground">
                    {/^https?:\/\//.test(s.ref)
                      ? <a href={s.ref} target="_blank" rel="noreferrer" className="text-link underline-offset-2 hover:underline">{new URL(s.ref).hostname}</a>
                      : <span className="text-foreground">{s.ref.startsWith('upstream:') ? graph.nodes.find((n) => `upstream:${n.node_key}` === s.ref)?.name ?? s.ref : s.ref === 'answer' ? 'Your answers' : s.ref}</span>}
                    {' · '}{s.detail}
                  </p>
                ))}
              </div>
            )}
          </div>
        )}
        {/* The design system's Section footer: last updated, and the section
            action. Approving sits here, after the draft text, so the draft
            is read through before it's approved; then the way on to the next
            section (in the sidebar's order; Generate package after the last),
            back at the top of the page. Both are always there, each enabled
            only when it applies. */}
        <div className="flex min-h-14 items-center justify-between gap-3 px-4 py-2 text-sm text-muted-foreground">
          <span>Last updated {timeAgo(node.updated_at)}</span>
          <div className="flex items-center gap-2">
            <Button data-tour="approve" variant={node.status === 'review' ? 'default' : 'outline'}
              disabled={busy || node.status !== 'review' || !open || editing}
              title={node.status === 'done' ? 'Approved' : node.status !== 'review' ? 'Ready to approve once it is drafted and its questions are answered'
                : editing ? 'Finish editing first' : !open ? 'Open the draft and read it first' : undefined}
              onClick={() => act(() => transitApi.approveNode(transitionId, node.node_key))} className={button}>
              <Check className="h-4 w-4" /> {node.status === 'done' ? 'Approved' : 'Approve section'}
            </Button>
            <Button data-tour="next-section" variant={node.status === 'done' ? 'default' : 'outline'} disabled={node.status !== 'done' || editing}
              title={node.status === 'done' ? (nextSection ? nextSection.name : 'Generate package') : 'Approve this section first'}
              onClick={(e) => {
                e.currentTarget.closest('.overflow-y-auto')?.scrollTo({ top: 0 })
                onSelect(nextSection?.node_key ?? PACKAGE)
              }} className={button}>
              {nextSection ? 'Go to next section' : 'Go to Generate package'} <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  )
}

function NodeLinks({ label, nodes, onSelect }: { label: string; nodes: GraphNode[]; onSelect: (key: string) => void }) {
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      {label}
      {nodes.map((n) => (
        <button key={n.node_key} type="button" onClick={() => onSelect(n.node_key)}
          className="inline-flex items-center gap-1.5 rounded-md bg-secondary px-2 py-1 leading-4 text-foreground transition-colors hover:bg-secondary-hover">
          <StatusIcon status={n.status} className="h-3 w-3" />
          {n.name}
        </button>
      ))}
    </span>
  )
}

/**
 * Generate package: its header (PackageSection: generate once every section is
 * approved, download any version), then the whole document as it stands, every
 * section in package order and numbered as the package numbers them (an
 * Appendix isn't), each with its status and a link back to it.
 */
function PackageView({ transitionId, graph, onSelect, onOpenFigures }: {
  transitionId: string; graph: TransitionGraph; onSelect: (key: string) => void; onOpenFigures: (key: string) => void
}) {
  const sections = [...graph.nodes].sort((a, b) => a.position - b.position)
  const numbered = sections.filter((n) => !n.name.startsWith('Appendix'))
  const title = (n: GraphNode) => n.name.startsWith('Appendix') ? n.name : `${numbered.indexOf(n) + 1}. ${n.name}`
  return (
    <div className="flex flex-col gap-6">
      <PackageSection transitionId={transitionId} graph={graph} />
      <article aria-label="The package as it stands" className="flex flex-col gap-10 rounded-xl border border-border bg-card px-8 py-6">
        {sections.map((n) => (
          <section key={n.node_key} className="flex flex-col gap-4">
            <div className="flex items-start justify-between gap-4 border-b border-border-subtle pb-3">
              <h2 className="text-xl font-semibold leading-[1.3] text-foreground">{title(n)}</h2>
              <div className="flex shrink-0 items-center gap-3">
                <span className={cn('inline-flex items-center gap-1.5 rounded-[4px] px-2 py-1 text-sm leading-4', nodeChipClass(n.status))}>
                  <StatusIcon status={n.status} plain labelled />{nodeStatusLabel(n.status)}
                </span>
                <button type="button" onClick={() => onSelect(n.node_key)}
                  className="text-sm text-foreground underline-offset-2 hover:underline">Open section</button>
              </div>
            </div>
            <DraftBody node={n} onReviewFigures={() => onOpenFigures(n.node_key)} />
          </section>
        ))}
      </article>
    </div>
  )
}

/** What a waited-on section needs before the one waiting on it can draft. */
function needs(n: GraphNode, open: number): string {
  switch (n.status) {
    case 'needs_input': return open > 0 ? `Answer ${open} question${open === 1 ? '' : 's'}` : 'Needs input'
    case 'review': return 'Read and approve it'
    case 'drafting': return 'Drafting now'
    case 'ready': return 'Ready to draft'
    case 'failed': return 'Its draft failed: draft it again'
    case 'blocked': return 'Waiting on its own earlier sections'
    default: return ''
  }
}

/**
 * A blocked section: what has to happen before it can draft, one row per
 * section it waits on that isn't approved yet, each linking to it. A notice,
 * in the Waiting chip's neutral, apart from the panel openers' green.
 */
function BlockedNotice({ waitsOn, openByNode, onSelect }: {
  waitsOn: GraphNode[]; openByNode: Map<string, number>; onSelect: (key: string) => void
}) {
  const left = waitsOn.filter((n) => n.status !== 'done')
  const approved = waitsOn.length - left.length
  return (
    <div role="note" className="flex w-full max-w-[560px] flex-col gap-3 rounded-md border border-border bg-muted/60 p-3 text-foreground">
      <span className="flex items-center gap-1 text-sm font-medium leading-[1.5] text-muted-foreground">
        <Hourglass className="h-4 w-4" aria-hidden /> Waiting on
      </span>
      <span className="flex flex-col gap-1 leading-[1.5]">
        <span className="text-base font-medium">
          {left.length === 1 ? 'One section to approve first' : `${left.length} sections to approve first`}
        </span>
        <span className="text-sm text-muted-foreground">
          This section drafts on its own once {left.length === 1 ? 'it is' : 'these are'} approved
          {approved > 0 ? ` (${approved} of ${waitsOn.length} already are)` : ''}.
        </span>
      </span>
      <ul className="flex flex-col divide-y divide-border-subtle rounded-md bg-card">
        {left.map((n) => (
          <li key={n.node_key}>
            <button type="button" onClick={() => onSelect(n.node_key)}
              className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm transition-colors hover:bg-secondary">
              <StatusIcon status={n.status} />
              <span className="min-w-0 flex-1 font-medium text-foreground">{n.name}</span>
              <span className="shrink-0 text-muted-foreground">{needs(n, openByNode.get(n.node_key) ?? 0)}</span>
              <SvgIcon src="/transit/icon-chevron-right.svg" width={14} height={14} className="shrink-0 text-tertiary" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * A section in review: a notice, not a button. Approve section stays at the
 * end of the draft so the draft is read first; nothing here takes the user
 * there. In the Review chip's teal, apart from the panel openers' green.
 */
function ApprovalNotice() {
  return (
    <div role="note" className="flex w-full max-w-[420px] flex-col gap-3 rounded-md border border-state-accent-text/30 bg-state-accent-subtle p-3 text-state-accent-text">
      <span className="flex items-center gap-1 text-sm font-medium leading-[1.5]">
        <CircleCheck className="h-4 w-4" aria-hidden /> Approval
      </span>
      <span className="flex flex-col gap-1 leading-[1.5]">
        <span className="text-base font-medium">Ready for approval</span>
        <span className="text-sm opacity-90">This section can be approved. Please read all of its content, then approve it at the bottom of the page.</span>
      </span>
    </div>
  )
}

/** An action item ("Guided questions", "Cost figures"): opens its side panel tab. */
function ActionCard({ label, title, body, onOpen }: { label: string; title: string; body: string; onOpen: () => void }) {
  return (
    <button type="button" onClick={onOpen}
      className="flex w-full max-w-[420px] flex-col gap-3 rounded-md bg-action-item p-3 text-left text-action-item-foreground transition-opacity hover:opacity-95">
      <span className="flex w-full items-center justify-between">
        <span className="flex items-center gap-1 text-sm font-medium leading-[1.5]">
          <SvgIcon src="/transit/icon-list.svg" width={16} height={16} />
          {label}
        </span>
        <SvgIcon src="/transit/icon-chevron-right.svg" width={16} height={16} />
      </span>
      <span className="flex flex-col gap-1 leading-[1.5]">
        <span className="text-base font-medium">{title}</span>
        <span className="text-sm opacity-90">{body}</span>
      </span>
    </button>
  )
}
const levelTone = (v: number | null, ladder: 'crl' | 'tech') =>
  v == null ? 'text-muted-foreground'
    : ladder === 'crl' ? (v >= 4 ? 'text-success' : v === 3 ? 'text-warning' : 'text-destructive')
      : (v >= 7 ? 'text-success' : v >= 5 ? 'text-warning' : 'text-destructive')

/** The computed assessment: every level here comes from code, under framework v4.0. */
function ReadinessCard({ transitionId }: { transitionId: string }) {
  const [rd, setRd] = useState<Readiness | null>(null)
  const [error, setError] = useState<string | null>(null)
  const load = useCallback(() => {
    transitApi.getReadiness(transitionId).then(setRd).catch((e) => setError(e.message))
  }, [transitionId])
  useEffect(() => { load() }, [load])
  const running = rd?.job && ['available', 'running', 'retryable', 'scheduled'].includes(rd.job.state)
  useEffect(() => {
    if (!running) return
    const id = setInterval(load, 2000)
    return () => clearInterval(id)
  }, [running, load])

  const assess = async () => {
    setError(null)
    try { await transitApi.assessReadiness(transitionId); load() } catch (e: any) { setError(e.message) }
  }
  const event = async () => {
    const what = window.prompt('Trigger event (e.g. financing event, key departure, CMMC result):')
    if (!what?.trim()) return
    try { await transitApi.recordReadinessEvent(transitionId, what, ''); load() } catch (e: any) { setError(e.message) }
  }
  const r = rd?.result

  return (
    <section data-tour="readiness" className="rounded-xl border border-border bg-card p-5 space-y-4 text-sm">
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex-1 min-w-[240px]">
          <h2 className="font-semibold text-foreground">Readiness</h2>
          <p className="text-muted-foreground">
            The model only reads facts, each with its source; the platform computes every level under the Technology
            Transition Framework v4.0. The CRL is the lowest pillar, never the average.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={event} disabled={!rd?.assessed}>Record event</Button>
        <Button size="sm" onClick={assess} disabled={!!running}>
          {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gauge className="h-4 w-4" />}
          {running ? 'Assessing…' : rd?.assessed ? 'Assess again' : 'Assess'}
        </Button>
      </div>
      {error && <div className="text-xs text-destructive">{error}</div>}
      {rd?.job?.error && rd.job.state !== 'completed' && (
        <div className="text-xs text-destructive">Last assessment {rd.job.state}: {rd.job.error}</div>
      )}
      {!rd ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : !r ? (
        <p className="text-muted-foreground">Not assessed yet. It runs with the first drafts, or now with Assess.</p>
      ) : (
        <>
          <p className={`text-xs ${r.staleness === 'current' && !rd.out_of_date ? 'text-muted-foreground' : 'text-destructive'}`}>
            As of {new Date(r.as_of).toLocaleDateString()}, valid until {new Date(r.valid_until).toLocaleDateString()} ({r.staleness}
            {r.stale_why ? `: ${r.stale_why}` : ''}){rd.out_of_date ? '. A document changed since: assess again.' : '.'}
          </p>
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-xs">
              <thead className="bg-muted text-left">
                <tr>{['Dimension', 'Declared', 'Derived', 'Verdict', 'Confidence', 'Next level needs'].map((h) => <th key={h} className="px-2 py-1.5">{h}</th>)}</tr>
              </thead>
              <tbody>
                {r.ladders.map((l) => (
                  <tr key={l.name} className="border-t border-border align-top">
                    <td className="px-2 py-1.5 font-medium">{l.name}</td>
                    <td className="px-2 py-1.5">{l.declared ?? '—'}</td>
                    <td className={`px-2 py-1.5 font-semibold ${levelTone(l.derived, 'tech')}`}>{l.derived ?? 'not established'}</td>
                    <td className="px-2 py-1.5">{l.verdict.replace(/_/g, ' ')}</td>
                    <td className="px-2 py-1.5">{l.confidence}</td>
                    <td className="px-2 py-1.5 text-muted-foreground">{l.next_level || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="rounded-md border-l-4 border-primary bg-accent px-3 py-2">
            <span className={`font-semibold ${levelTone(r.crl.level, 'crl')}`}>CRL {r.crl.level ?? 'not scored'}{r.crl.status ? `, ${r.crl.status}` : ''}.</span>{' '}
            {r.crl.reading}{r.crl.binding?.length ? ` Binding: ${r.crl.binding.join(', ')}.` : ''}
          </div>
          <div className="grid gap-3 md:grid-cols-3">
            {r.crl.pillars.map((p) => (
              <div key={p.id} className="rounded-md border border-border p-3 space-y-1">
                <div className="flex items-center justify-between">
                  <span className="font-medium">{p.name}</span>
                  <span className={`font-semibold ${levelTone(p.level, 'crl')}`}>{p.level ?? '—'}</span>
                </div>
                <ul className="space-y-0.5 text-xs">
                  {p.signals.map((s) => (
                    <li key={s.signal} title={s.basis}>
                      <span className="text-muted-foreground">{s.signal}:</span> {s.value}
                      {s.ref && <span className="text-muted-foreground"> ({s.verifiability})</span>}
                    </li>
                  ))}
                </ul>
                {p.unmet && p.unmet.length > 0 && <p className="text-xs text-muted-foreground">Next level needs: {p.unmet.join('; ')}</p>}
              </div>
            ))}
          </div>
          <div className="rounded-md border border-border px-3 py-2">
            <span className="font-semibold">{r.matrix.cell || 'Matrix cell not determined'}:</span> {r.matrix.action}{' '}
            <span className="text-muted-foreground">{r.matrix.instruments}</span>
          </div>
          <ul className="space-y-1 text-xs">
            {r.red_flags.map((f) => (
              <li key={f.id} className={f.flagged ? 'text-destructive' : 'text-muted-foreground'}>
                <span className="font-medium">{f.label}:</span> {f.finding}
              </li>
            ))}
          </ul>
          {rd.dropped.length > 0 && (
            <details className="text-xs text-muted-foreground">
              <summary>{rd.dropped.length} extracted fact{rd.dropped.length === 1 ? '' : 's'} cleared (no real source, or an unknown value)</summary>
              <ul className="list-disc pl-5">{rd.dropped.map((d, i) => <li key={i}>{d}</li>)}</ul>
            </details>
          )}
        </>
      )}
    </section>
  )
}

/** Edit a section's blocks in place: text, bullets (one per line) and table cells. */
function BlockEditor({ blocks, busy, onSave, onCancel }: {
  blocks: DraftBlock[]; busy: boolean; onSave: (b: DraftBlock[]) => void; onCancel: () => void
}) {
  const [draft, setDraft] = useState<DraftBlock[]>(() => JSON.parse(JSON.stringify(blocks)))
  const set = (i: number, b: DraftBlock) => setDraft((d) => d.map((x, k) => (k === i ? b : x)))
  const field = 'w-full rounded-md border border-border bg-background px-2 py-1 text-sm'
  return (
    <div className="space-y-3">
      {draft.map((b, i) => (
        <div key={i} className="space-y-1">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{b.type}</div>
          {b.type === 'callout' && <input className={field} value={b.label ?? ''} onChange={(e) => set(i, { ...b, label: e.target.value })} />}
          {(b.type === 'paragraph' || b.type === 'callout') && (
            <Textarea rows={3} value={b.text ?? ''} onChange={(e) => set(i, { ...b, text: e.target.value })} />
          )}
          {b.type === 'heading' && <input className={field} value={b.text ?? ''} onChange={(e) => set(i, { ...b, text: e.target.value })} />}
          {b.type === 'bullets' && (
            <Textarea rows={Math.max(2, b.items?.length ?? 0)} value={(b.items ?? []).join('\n')}
              onChange={(e) => set(i, { ...b, items: e.target.value.split('\n') })} />
          )}
          {b.type === 'table' && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr>{b.columns?.map((c, j) => <th key={j} className="px-1 py-1 text-left">{c}</th>)}<th /></tr></thead>
                <tbody>
                  {b.rows?.map((r, j) => (
                    <tr key={j}>
                      {r.map((cell, k) => (
                        <td key={k} className="px-1 py-0.5">
                          <input className={field + ' text-xs'} value={cell}
                            onChange={(e) => set(i, { ...b, rows: b.rows!.map((rr, jj) => (jj === j ? rr.map((cc, kk) => (kk === k ? e.target.value : cc)) : rr)) })} />
                        </td>
                      ))}
                      <td><button type="button" className="text-xs text-muted-foreground hover:text-destructive"
                        onClick={() => set(i, { ...b, rows: b.rows!.filter((_, jj) => jj !== j) })}>Remove</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button type="button" className="mt-1 text-xs text-foreground underline-offset-2 hover:underline"
                onClick={() => set(i, { ...b, rows: [...(b.rows ?? []), (b.columns ?? []).map(() => '')] })}>Add row</button>
            </div>
          )}
        </div>
      ))}
      <div className="flex gap-2">
        <Button size="sm" disabled={busy} onClick={() => onSave(draft.map((b) => (b.type === 'bullets' ? { ...b, items: (b.items ?? []).filter((x) => x.trim()) } : b)))}>
          Save edits
        </Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  )
}

/** A section's draft as read (the section page, unless editing, and the package
 * view): its blocks, else its plain text, else what it's waiting for. */
function DraftBody({ node, onReviewFigures }: { node: GraphNode; onReviewFigures: () => void }) {
  if (node.status === 'drafting' && !node.content) {
    return <p className="flex items-center gap-2 text-sm text-tertiary"><Loader2 className="h-4 w-4 animate-spin" /> Drafting from the documents…</p>
  }
  if (node.data?.blocks?.length) {
    return <DraftBlocks blocks={node.data.blocks} confirmedRows={node.confirmed_rows} onReviewFigures={onReviewFigures} />
  }
  if (node.content) {
    return <div className="whitespace-pre-wrap text-sm leading-[1.5] text-muted-foreground"><Marked text={node.content} /></div>
  }
  return (
    <p className="text-sm text-tertiary">
      {node.status === 'blocked' ? 'Waits on the sections it depends on before it can draft.' : 'Not drafted yet.'}
    </p>
  )
}

/** Text with the drafts' "[NEEDS INPUT]" marker (blocks.NeedsInput in Go) shown as a chip. */
function Marked({ text }: { text?: string }) {
  const parts = (text ?? '').split('[NEEDS INPUT]')
  return (
    <>
      {parts.map((part, i) => (
        <span key={i}>
          {i > 0 && (
            <mark className="mr-1 rounded-[4px] bg-status-attention px-1.5 py-0.5 text-xs font-medium text-status-foreground">Needs input</mark>
          )}
          {part}
        </span>
      ))}
    </>
  )
}

/**
 * A draft's blocks, in the Bridge section style: each heading starts an
 * element, set off by a left rule with its blocks under it. Block indexes
 * are kept as-is, since confirmed table rows are keyed by them.
 */
function DraftBlocks({ blocks, confirmedRows = [], onReviewFigures }: {
  blocks: DraftBlock[]; confirmedRows?: string[]
  /** Open the side panel's Cost figures tab, where summed rows are confirmed. */
  onReviewFigures: () => void
}) {
  const done = new Set(confirmedRows)
  const elements: Array<{ heading?: string; items: Array<{ block: DraftBlock; index: number }> }> = []
  blocks.forEach((block, index) => {
    if (block.type === 'heading') elements.push({ heading: block.text, items: [] })
    else {
      if (elements.length === 0) elements.push({ items: [] })
      elements[elements.length - 1].items.push({ block, index })
    }
  })

  const render = (b: DraftBlock, i: number) => {
    switch (b.type) {
      case 'callout':
        return (
          <div key={i} className="rounded-md bg-secondary px-4 py-3 text-sm leading-[1.5] text-foreground">
            {b.label && <span className="font-medium">{b.label} </span>}<Marked text={b.text} />
          </div>
        )
      case 'bullets':
        return (
          <ul key={i} className="list-disc space-y-1 pl-5 text-sm leading-[1.5] text-muted-foreground">
            {b.items?.map((it, j) => <li key={j}><Marked text={it} /></li>)}
          </ul>
        )
      case 'table':
        return (
          <div key={i} className="overflow-x-auto rounded-md border border-input">
            <table className="w-full text-sm">
              {/* The design system's Table cell: a Medium header on
                  bg/surface-subtle, Regular body rows in text/secondary. */}
              <thead className="bg-muted text-left text-foreground">
                <tr>
                  {b.columns?.map((c, j) => <th key={j} className="px-3 py-2.5 font-medium">{c}</th>)}
                </tr>
              </thead>
              <tbody>
                {b.rows?.map((r, j) => (
                  <tr key={j} className="border-t border-border-subtle align-top">
                    {r.map((cell, k) => <td key={k} className="px-3 py-3.5 text-muted-foreground"><Marked text={cell} /></td>)}
                  </tr>
                ))}
              </tbody>
            </table>
            {b.totals && (() => {
              // Confirming happens in the side panel's Cost figures tab.
              const rows = b.rows?.length ?? 0
              const left = rows - [...done].filter((k) => k.startsWith(`${i}:`)).length
              return (
                <div className="flex items-center justify-between gap-3 border-t border-border-subtle px-3 py-2 text-xs text-tertiary">
                  <span>
                    {left > 0 ? `${left} of ${rows} figures unconfirmed.` : `All ${rows} figures confirmed.`}{' '}
                    Totals by {b.totals.by || 'table'} are computed in the package.
                  </span>
                  <button type="button" onClick={onReviewFigures} className="shrink-0 font-medium text-foreground underline-offset-2 hover:underline">
                    {left > 0 ? 'Confirm figures' : 'Review figures'}
                  </button>
                </div>
              )
            })()}
          </div>
        )
      default:
        return <p key={i} className="text-sm leading-[1.5] text-muted-foreground"><Marked text={b.text} /></p>
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {elements.map((el, k) => el.heading === undefined ? (
        <div key={k} className="flex flex-col gap-3">{el.items.map(({ block, index }) => render(block, index))}</div>
      ) : (
        <div key={k} className="flex flex-col gap-2 border-l border-input pl-4">
          <h3 className="text-sm font-medium leading-[1.5] text-tertiary">{el.heading}</h3>
          {el.items.map(({ block, index }) => render(block, index))}
        </div>
      ))}
    </div>
  )
}

/** The document's input type; the user's choice is never overridden. */